const debouncePromise = (fn, time) => {
  let timerId = undefined;

  return (...args) => {
    if (timerId) {
      clearTimeout(timerId);
    }

    return new Promise((resolve) => {
      timerId = setTimeout(() => resolve(fn(...args)), time);
    });
  };
};

const SearchBox = ({
  placeholder,
  translations,
  debounceMs,
  algoliaIndexName,
  algoliaApplicationId,
  algoliaSearchApiKey
}) => ({
  init() {
    (async () => {
      const { default: algoliasearch } = await import('algoliasearch/lite');

      const searchClient = algoliasearch(
        algoliaApplicationId,
        algoliaSearchApiKey
      );

      const { autocomplete, getAlgoliaResults } = await import(
        '@algolia/autocomplete-js'
      );

      const debounced = debouncePromise(
        (items) => Promise.resolve(items),
        debounceMs
      );

      autocomplete({
        container: this.$refs.search,

        autoFocus: true,

        panelContainer: this.$root.closest('dialog') || document.body,

        placeholder,
        translations,

        detachedMediaQuery: 'none',

        onSubmit({ state }) {
          const url = new URL('/search', window.location.origin);
          url.searchParams.set(`${algoliaIndexName}[query]`, state.query);
          window.location.href = url.toString();
        },

        onStateChange: (state) => {
          this.$store.navigation.isSearchOpen = state.state.isOpen;
        },

        getSources({ query }) {
          return debounced([
            {
              sourceId: 'pages',
              getItems() {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: algoliaIndexName,
                      query,
                      params: {
                        hitsPerPage: 5,
                        attributesToSnippet: ['content']
                      }
                    }
                  ]
                });
              },
              templates: {
                item({ item, components, html }) {
                  return html`
                    <li class="list-item">
                      <a class="list-item__link" href="${item.url}">
                        <div class="list-item__accent"></div>
                        <div>
                          <div class="list-item__title typo-copy">
                            <strong>
                              ${item.categories.lvl0}:${' '}
                              ${components.Highlight({
                                hit: item,
                                attribute: 'title'
                              })}
                            </strong>
                          </div>
                          <div class="typo-copy">
                            ${components.Snippet({
                              hit: item,
                              attribute: 'content'
                            })}
                          </div>
                        </div>
                      </a>
                    </li>
                  `;
                }
              }
            }
          ]);
        }
      });

      const ClearIcon = this.$root.querySelector('.aa-ClearIcon');
      ClearIcon.replaceWith(this.$refs.ClearIcon.content.cloneNode(true));

      const SubmitIcon = this.$root.querySelector('.aa-SubmitIcon');
      SubmitIcon.replaceWith(this.$refs.SubmitIcon.content.cloneNode(true));

      /**
       * Last scroll position of the main page (not the navigation dialog)
       * @type {number}
       */
      let lastScrollY = 0;

      /**
       * HDK-93 Reset the main page scroll position to 0 while the navigation dialog is opened
       *
       * The autocomplete library positions its panel according to the
       * page scrollY. However, as the dialog element has its own scroll
       * context, this logic results in the panel being wrongly positioned
       * if the main page is scrolled.
       *
       * As a workaround, we here reset the page scroll position to 0.
       *
       * See https://github.com/algolia/autocomplete/blob/e548901afa92871b5b92ef5339ef4c9bf85ec16e/packages/autocomplete-js/src/getPanelPlacementStyle.ts#L25
       */
      this.$watch('$store.navigation.isOpen', (isOpen) => {
        if (isOpen) {
          lastScrollY = window.scrollY;
          window.scrollTo({ top: 0 });
        } else {
          this.$refs.search.querySelector('.aa-Form').reset();
          window.scrollTo({ top: lastScrollY });
        }
      });
    })();
  }
});

export default SearchBox;
